import { useEffect, useState } from 'react'
import { whenVideoPainted } from '../utils/whenVideoPainted'

/**
 * SC-158: the raw view kept **beneath** a camera preview while a pick made
 * over it takes the preview over (see `previewHandoverFrom` in
 * useMediaDevices).
 *
 * The first processed stream has to be bound to the preview element, and
 * binding a stream blanks an element until it decodes. Covering that moment
 * shows a flat panel where the user was just looking at themselves; this keeps
 * the view they were looking at beneath it instead. It is bound while the
 * pick is held, so it has painted long before the processed stream arrives,
 * and shown only while the element above it has not painted that stream yet.
 *
 * `underlay` is a second `<video>` stacked under the preview element, `stream`
 * the raw view it may hold (null: none, and it is unbound), `previewStream`
 * what the element above it is bound to. Returns whether the underlay is what
 * shows. False until it has painted a frame of its own: a blank underlay is no
 * better than the cover, which then stands in as before.
 */
export function usePreviewHandoverUnderlay(
  underlay: HTMLVideoElement | null,
  stream: MediaStream | null,
  previewStream: MediaStream | null
): boolean {
  /**
   * What the underlay has painted, by identity, as the previews track theirs,
   * with the element: a dialog that reopens brings a new, blank one.
   */
  const [painted, setPainted] = useState<{
    element: HTMLVideoElement
    stream: MediaStream
  } | null>(null)

  useEffect(() => {
    if (!underlay) return undefined
    underlay.srcObject = stream
    if (!stream) {
      // Unbound, so a later hold over the same stream starts unpainted. No lag
      // to worry about here: with no stream the underlay is not shown anyway.
      setPainted(null)
      return undefined
    }
    underlay.play().catch(() => {
      /* never painted, so never shown: the cover stands in */
    })

    // A composited frame, as the previews wait for; `loadeddata` where there
    // is no frame callback (Firefox). No timeout: an underlay that never paints
    // is never shown, and the cover stands in.
    return whenVideoPainted(underlay, { frames: 1, fallbackEvents: ['loadeddata'] }, () =>
      setPainted({ element: underlay, stream })
    )
  }, [underlay, stream])

  return (
    stream !== null &&
    previewStream !== stream &&
    painted?.element === underlay &&
    painted.stream === stream
  )
}

export default usePreviewHandoverUnderlay
